import { Moon, Sun, LogOut, Shield, User as UserIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth, isAdmin } from "@/lib/auth";
import UsersManager from "@/components/UsersManager";

const LOGO_URL =
  "https://customer-assets-agu9un31.emergentagent.net/job_sales-tracker-1336/artifacts/xrne8mgy_versao_branca_03.webp";

export default function Header({ theme, setTheme }) {
  const { user, logout } = useAuth();
  const [usersOpen, setUsersOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 glass border-b border-border">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-[hsl(236,78%,32%)] grid place-items-center overflow-hidden shadow-lg shadow-primary/25">
            <div className="absolute inset-0 opacity-40"
              style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'><path d='M0 24 Q 10 16 20 24 T 40 24' stroke='white' stroke-opacity='0.35' stroke-width='1' fill='none'/><path d='M0 30 Q 10 22 20 30 T 40 30' stroke='white' stroke-opacity='0.25' stroke-width='1' fill='none'/></svg>\")" }} />
            <img src={LOGO_URL} alt="FLUA" className="relative h-6 w-auto" data-testid="brand-logo" />
          </div>
          <div>
            <div className="font-heading font-extrabold tracking-tight text-base leading-none">FLUA</div>
            <div className="text-[9px] uppercase tracking-[0.24em] text-muted-foreground mt-1 font-medium">
              Highconnection Internet
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            data-testid="theme-toggle-button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label="Alternar tema"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="user-menu-trigger" className="gap-2">
                <div className="w-6 h-6 rounded-full bg-primary/15 text-primary grid place-items-center">
                  {isAdmin(user) ? <Shield className="w-3.5 h-3.5" /> : <UserIcon className="w-3.5 h-3.5" />}
                </div>
                <span className="hidden sm:inline text-sm max-w-[140px] truncate">{user?.name || user?.email}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <div className="font-semibold">{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                <Badge variant="outline" className={`mt-2 ${isAdmin(user) ? "bg-primary/15 text-primary border-primary/40" : ""}`}>
                  {user?.role}
                  {!isAdmin(user) && user?.vendedor_name ? ` · ${user.vendedor_name}` : ""}
                </Badge>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isAdmin(user) && (
                <DropdownMenuItem onClick={() => setUsersOpen(true)} data-testid="menu-users">
                  <Shield className="w-4 h-4 mr-2" /> Gerenciar usuários
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => { logout(); toast.success("Você saiu"); }} data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <UsersManager open={usersOpen} onOpenChange={setUsersOpen} />
    </header>
  );
}
